/**
 * useOSDPricing Hook
 *
 * Manages OSD (Operator Systemu Dystrybucyjnego) selection and provides
 * pricing data for the selected operator.
 *
 * Features:
 * - Persists OSD selection in localStorage
 * - Provides available tariffs for selected OSD
 * - Provides pricing data for calculations
 *
 * @see /frontend/src/utils/osdMetadata.ts - OSD metadata
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { TariffType } from '../types/graphql';
import {
  OSDCode,
  OSD_METADATA,
  getDefaultOSD,
  getAvailableTariffsForOSD,
  isValidOSDCode,
} from '../utils/osdMetadata';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Pricing data for a single tariff
 */
export interface TariffPricing {
  /** Energy price for flat rate tariffs (G11) */
  flat?: number;
  /** Day energy price */
  day?: number;
  /** Night energy price */
  night?: number;
  /** Peak energy price */
  peak?: number;
  /** Off-peak energy price */
  offpeak?: number;
  /** Weekend energy price */
  weekend?: number;
  /** Weekday energy price */
  weekday?: number;
  /** Solar hours energy price (G13s) */
  solar?: number;
  /** Other hours energy price (G13s) */
  other?: number;
}

/**
 * Complete pricing data for an OSD
 */
export interface OSDPricingData {
  osd: OSDCode;
  year: number;
  tariffs: Partial<Record<TariffType, TariffPricing>>;
}

/**
 * Hook return type
 */
export interface UseOSDPricingReturn {
  /** Currently selected OSD */
  selectedOSD: OSDCode;
  /** Change selected OSD */
  setSelectedOSD: (osd: OSDCode) => void;
  /** Available tariffs for selected OSD */
  availableTariffs: TariffType[];
  /** Check if a tariff is available for selected OSD */
  isTariffAvailable: (tariff: TariffType) => boolean;
  /** Get pricing data for selected OSD */
  getPricing: () => OSDPricingData;
  /** Get pricing for a specific tariff */
  getTariffPricing: (tariff: TariffType) => TariffPricing | null;
  /** OSD metadata for selected OSD */
  osdMetadata: typeof OSD_METADATA[OSDCode];
  /** Reset to default OSD */
  resetToDefault: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEY = 'ecompanion_selected_osd';
const DEFAULT_YEAR = 2026;

/**
 * OSD Pricing Data for 2026
 *
 * All prices are GROSS (with VAT 23%) per kWh in PLN
 * Calculated from URE-approved 2026 tariffs:
 * - Energy price + Distribution price + Quality charge + OZE fee + Cogeneration fee
 * - Then multiplied by 1.23 (VAT 23%)
 *
 * Source: /amplify/data/config/pricing-{osd}-2026.json
 * Last updated: 2026-01-31
 *
 * @see https://www.ure.gov.pl/ - Polish Energy Regulatory Office
 */
const OSD_PRICING: Record<OSDCode, Partial<Record<TariffType, TariffPricing>>> = {
  /**
   * TAURON Dystrybucja S.A. - 2026 Pricing
   * Available tariffs: G11, G12, G12w, G12as, G13, G13s, G14
   * Coverage: Dolnośląskie, Małopolskie, Opolskie, Śląskie, Świętokrzyskie (partial)
   */
  TAURON: {
    // G11: 0.4025 + 0.2779 + 0.0351 + 0.0073 + 0.00436 = 0.72716 net → 0.89 gross
    [TariffType.G11]: { flat: 0.89 },
    // G12 day: 0.4634 + 0.3170 + 0.0351 + 0.0073 + 0.00436 = 0.82716 → 1.02
    // G12 night: 0.3089 + 0.0666 + 0.0351 + 0.0073 + 0.00436 = 0.42226 → 0.52
    [TariffType.G12]: { day: 1.02, night: 0.52 },
    // G12as day: 0.4025 + 0.2783 + 0.0351 + 0.0073 + 0.00436 = 0.72756 → 0.90
    // G12as night: 0.4025 + 0.0212 + 0.0351 + 0.0073 + 0.00436 = 0.47046 → 0.58
    [TariffType.G12as]: { day: 0.90, night: 0.58 },
    // G12w weekday: 0.4025 + 0.3634 + 0.0351 + 0.0073 + 0.00436 = 0.81266 → 1.00
    // G12w weekend: 0.4025 + 0.0257 + 0.0351 + 0.0073 + 0.00436 = 0.47496 → 0.58
    [TariffType.G12w]: { weekday: 1.00, weekend: 0.58 },
    [TariffType.G12n]: { day: 1.00, night: 0.58 }, // Not available at TAURON, fallback to G12w
    [TariffType.G12r]: { day: 1.00, night: 0.58 }, // Not available at TAURON, fallback to G12w
    // G13 peak: 0.4025 + 0.3735 + 0.0351 + 0.0073 + 0.00436 = 0.82276 → 1.01
    // G13 off-peak: 0.4025 + 0.0257 + 0.0351 + 0.0073 + 0.00436 = 0.47496 → 0.58
    [TariffType.G13]: { peak: 1.01, offpeak: 0.58, night: 0.58 },
    // G13s solar: 0.4025 + 0.0437 + 0.0171 + 0.0073 + 0.00436 = 0.47496 → 0.58
    // G13s other: 0.4025 + 0.3172 + 0.0171 + 0.0073 + 0.00436 = 0.74856 → 0.92
    [TariffType.G13s]: { solar: 0.58, other: 0.92 },
    [TariffType.G14]: { flat: 0.90 }, // Dynamic RCE - average estimate based on 2026 data
  },
  /**
   * PGE Dystrybucja S.A. - 2026 Pricing
   * Available tariffs: G11, G12, G12w, G12n (PGE-specific)
   * Coverage: Mazowieckie, Łódzkie, Lubelskie, Podkarpackie, Podlaskie (partial)
   */
  PGE: {
    // G11: 0.4982 + 0.2686 + 0.0351 + 0.0073 + 0.00436 = 0.81356 net → 1.00 gross
    [TariffType.G11]: { flat: 1.00 },
    // G12 day: 0.4982 + 0.3039 + 0.0351 + 0.0073 + 0.00436 = 0.84866 → 1.04
    // G12 night: 0.3325 + 0.0998 + 0.0351 + 0.0073 + 0.00436 = 0.47906 → 0.59
    [TariffType.G12]: { day: 1.04, night: 0.59 },
    [TariffType.G12as]: { day: 1.04, night: 0.59 }, // Not available at PGE, fallback to G12
    // G12w peak: 0.4982 + 0.2955 + 0.0351 + 0.0073 + 0.00436 = 0.84046 → 1.03
    // G12w off-peak: 0.4982 + 0.0889 + 0.0351 + 0.0073 + 0.00436 = 0.63386 → 0.78
    [TariffType.G12w]: { weekday: 1.03, weekend: 0.78 },
    // G12n day: 0.4982 + 0.3039 + 0.0351 + 0.0073 + 0.00436 = 0.84866 → 1.04
    // G12n night: 0.3566 + 0.0998 + 0.0351 + 0.0073 + 0.00436 = 0.50316 → 0.62
    [TariffType.G12n]: { day: 1.04, night: 0.62 }, // PGE-specific: night 01:00-05:00 + weekends
    [TariffType.G12r]: { day: 1.04, night: 0.62 }, // Not available at PGE, fallback to G12n
    [TariffType.G13]: { peak: 1.04, offpeak: 0.62, night: 0.62 }, // Not available at PGE
    [TariffType.G13s]: { solar: 0.62, other: 1.00 }, // Not available at PGE
    [TariffType.G14]: { flat: 1.00 }, // Not available at PGE
  },
  /**
   * ENEA Operator sp. z o.o. - 2026 Pricing
   * Available tariffs: G11, G12, G12w, G12as
   * Coverage: Wielkopolskie, Zachodniopomorskie, Kujawsko-Pomorskie, Lubuskie
   */
  ENEA: {
    // G11: 0.4980 + 0.2613 + 0.0344 + 0.0073 + 0.00436 = 0.80536 net → 0.99 gross
    [TariffType.G11]: { flat: 0.99 },
    // G12 day: 0.4980 + 0.2966 + 0.0344 + 0.0073 + 0.00436 = 0.84066 → 1.03
    // G12 night: 0.3324 + 0.0972 + 0.0344 + 0.0073 + 0.00436 = 0.47566 → 0.58
    [TariffType.G12]: { day: 1.03, night: 0.58 },
    // G12as day: 0.4980 + 0.2966 + 0.0344 + 0.0073 + 0.00436 = 0.84066 → 1.03
    // G12as night: 0.4980 + 0.0705 + 0.0344 + 0.0073 + 0.00436 = 0.61456 → 0.76
    [TariffType.G12as]: { day: 1.03, night: 0.76 },
    // G12w peak: 0.4980 + 0.2892 + 0.0344 + 0.0073 + 0.00436 = 0.83326 → 1.02
    // G12w off-peak: 0.4980 + 0.0866 + 0.0344 + 0.0073 + 0.00436 = 0.63066 → 0.78
    [TariffType.G12w]: { weekday: 1.02, weekend: 0.78 },
    [TariffType.G12n]: { day: 1.03, night: 0.58 }, // Not available at ENEA, fallback to G12
    [TariffType.G12r]: { day: 1.03, night: 0.58 }, // Not available at ENEA, fallback to G12
    [TariffType.G13]: { peak: 1.03, offpeak: 0.58, night: 0.58 }, // Not available at ENEA
    [TariffType.G13s]: { solar: 0.58, other: 0.99 }, // Not available at ENEA
    [TariffType.G14]: { flat: 0.99 }, // Not available at ENEA
  },
  /**
   * ENERGA-OPERATOR S.A. - 2026 Pricing
   * Available tariffs: G11, G12, G12w, G12as, G12r (Energa-specific)
   * Coverage: Pomorskie, Warmińsko-Mazurskie, Kujawsko-Pomorskie (partial)
   * Note: Energa has the cheapest wholesale energy price (496.8 zł/MWh)
   */
  ENERGA: {
    // G11: 0.4968 + 0.2650 + 0.0348 + 0.0073 + 0.00436 = 0.80826 net → 0.99 gross
    [TariffType.G11]: { flat: 0.99 },
    // G12 day: 0.4968 + 0.3002 + 0.0348 + 0.0073 + 0.00436 = 0.84346 → 1.04
    // G12 night: 0.3316 + 0.0985 + 0.0348 + 0.0073 + 0.00436 = 0.47656 → 0.59
    [TariffType.G12]: { day: 1.04, night: 0.59 },
    // G12as day: 0.4968 + 0.3002 + 0.0348 + 0.0073 + 0.00436 = 0.84346 → 1.04
    // G12as night: 0.4968 + 0.0720 + 0.0348 + 0.0073 + 0.00436 = 0.61526 → 0.76
    [TariffType.G12as]: { day: 1.04, night: 0.76 },
    // G12w peak: 0.4968 + 0.2929 + 0.0348 + 0.0073 + 0.00436 = 0.83616 → 1.03
    // G12w off-peak: 0.4968 + 0.0876 + 0.0348 + 0.0073 + 0.00436 = 0.63086 → 0.78
    [TariffType.G12w]: { weekday: 1.03, weekend: 0.78 },
    [TariffType.G12n]: { day: 1.04, night: 0.59 }, // Not available at ENERGA, fallback to G12
    // G12r peak: 0.4968 + 0.2929 + 0.0348 + 0.0073 + 0.00436 = 0.83616 → 1.03
    // G12r off-peak: 0.4968 + 0.0779 + 0.0348 + 0.0073 + 0.00436 = 0.62116 → 0.76
    [TariffType.G12r]: { day: 1.03, night: 0.76 }, // Energa-specific: extended weekend hours
    [TariffType.G13]: { peak: 1.04, offpeak: 0.59, night: 0.59 }, // Not available at ENERGA
    [TariffType.G13s]: { solar: 0.59, other: 0.99 }, // Not available at ENERGA
    [TariffType.G14]: { flat: 0.99 }, // Not available at ENERGA
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Load OSD from localStorage
 */
function loadStoredOSD(): OSDCode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidOSDCode(stored)) {
      return stored;
    }
  } catch {
    // localStorage not available (SSR, incognito, etc.)
  }
  return getDefaultOSD();
}

/**
 * Save OSD to localStorage
 */
function saveOSDToStorage(osd: OSDCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, osd);
  } catch {
    // localStorage not available
  }
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * useOSDPricing Hook
 *
 * Manages OSD selection and provides pricing data.
 *
 * @param initialOSD - Optional initial OSD (overrides localStorage)
 * @returns OSD state and pricing utilities
 *
 * @example
 * ```tsx
 * const { selectedOSD, setSelectedOSD, availableTariffs, getTariffPricing } = useOSDPricing();
 *
 * // Get available tariffs for current OSD
 * const tariffs = availableTariffs; // e.g., ['G11', 'G12', 'G12w', 'G12as', 'G13', 'G13s', 'G14'] for TAURON
 *
 * // Get pricing for a tariff
 * const g12Pricing = getTariffPricing(TariffType.G12);
 * // { day: 1.20, night: 0.65 }
 * ```
 */
export function useOSDPricing(initialOSD?: OSDCode): UseOSDPricingReturn {
  // Initialize from prop, localStorage, or default
  const [selectedOSD, setSelectedOSDState] = useState<OSDCode>(() => {
    if (initialOSD && isValidOSDCode(initialOSD)) {
      return initialOSD;
    }
    return loadStoredOSD();
  });

  // Persist selection to localStorage
  useEffect(() => {
    saveOSDToStorage(selectedOSD);
  }, [selectedOSD]);

  // Available tariffs for selected OSD
  const availableTariffs = useMemo(
    () => getAvailableTariffsForOSD(selectedOSD),
    [selectedOSD]
  );

  // OSD metadata
  const osdMetadata = useMemo(
    () => OSD_METADATA[selectedOSD],
    [selectedOSD]
  );

  // Set OSD with validation
  const setSelectedOSD = useCallback((osd: OSDCode) => {
    if (isValidOSDCode(osd)) {
      setSelectedOSDState(osd);
    }
  }, []);

  // Check if tariff is available
  const isTariffAvailable = useCallback(
    (tariff: TariffType): boolean => {
      return availableTariffs.includes(tariff);
    },
    [availableTariffs]
  );

  // Get complete pricing data
  const getPricing = useCallback((): OSDPricingData => {
    return {
      osd: selectedOSD,
      year: DEFAULT_YEAR,
      tariffs: OSD_PRICING[selectedOSD],
    };
  }, [selectedOSD]);

  // Get pricing for a specific tariff
  const getTariffPricing = useCallback(
    (tariff: TariffType): TariffPricing | null => {
      if (!isTariffAvailable(tariff)) {
        return null;
      }
      return OSD_PRICING[selectedOSD][tariff] || null;
    },
    [selectedOSD, isTariffAvailable]
  );

  // Reset to default
  const resetToDefault = useCallback(() => {
    setSelectedOSDState(getDefaultOSD());
  }, []);

  return {
    selectedOSD,
    setSelectedOSD,
    availableTariffs,
    isTariffAvailable,
    getPricing,
    getTariffPricing,
    osdMetadata,
    resetToDefault,
  };
}

export default useOSDPricing;
